import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { ApiTagsEnum } from '@/enums/apiTags.enum';
import { TimePeriodEnum } from '@/enums/timePeriod.enum';

import {
  GetPickemsResponse,
  GetPickemSelectionsResponse,
  LoginReqBody,
  LoginResponse,
  MakeSelectionsReqBody,
  MakeSelectionsResponse,
  RulesResponse,
  UserLeaderboardPositionRes,
} from '@/types/domain';

import { baseQueryWithRedirect } from '@/utils/api';

export const api = createApi({
  reducerPath: 'mainApi',
  tagTypes: Object.values(ApiTagsEnum),
  baseQuery: baseQueryWithRedirect,
  endpoints: (builder) => ({
    // Auth
    login: builder.mutation<LoginResponse, LoginReqBody>({
      query: (body) => ({
        url: '/login',
        method: 'POST',
        body,
      }),
    }),
    refreshToken: builder.mutation<LoginResponse, { refreshToken: string }>({
      query: ({ refreshToken }) => ({
        url: '/refresh-token',
        method: 'POST',
        body: { refresh_token: refreshToken },
      }),
    }),

    // Pickems
    getPickemsList: builder.query<GetPickemsResponse, any>({
      query: ({ status }) => {
        return {
          url: '/pickems',
          params: {
            status,
            offset: 0,
            limit: 10,
          },
        };
      },
      providesTags: [ApiTagsEnum.PICKEMS],
    }),
    makeSelections: builder.mutation<
      MakeSelectionsResponse,
      MakeSelectionsReqBody
    >({
      query: ({ id, body }) => ({
        url: `/pickems/${id}/selection`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ApiTagsEnum.PICKEMS],
    }),
    getUserLeaderboardPosition: builder.query<
      UserLeaderboardPositionRes,
      { pickem_id: number; period: string | null }
    >({
      query: ({ pickem_id, period }) => ({
        url:
          !period || period === TimePeriodEnum.allTime
            ? `/pickem-leaderboard/${pickem_id}/user`
            : `/pickem-leaderboard/${pickem_id}/user/active`,
      }),
    }),
    getRules: builder.query<RulesResponse, { lang: string; pickemId: number }>({
      query: ({ lang, pickemId }) => ({
        url: `page/pickem-${pickemId}?lang=${lang}`,
      }),
    }),
    getPickemSelections: builder.query<GetPickemSelectionsResponse, number>({
      query: (id) => ({
        url: `/pickems/${id}/selections`,
      }),
      providesTags: [ApiTagsEnum.PICKEMS],
    }),
  }),
});

export const {
  // Auth
  useLoginMutation,
  useRefreshTokenMutation,

  // Pickems
  useGetPickemsListQuery,
  useGetPickemSelectionsQuery,
  useMakeSelectionsMutation,
  useGetUserLeaderboardPositionQuery,
  useGetRulesQuery,
} = api;
